import axios from "axios";

import {
  destinationCardUsageReadiness,
  provisionDestinationCard,
} from "../services/api";
import { registerPayoutCard } from "./register-payout-card";
import { isVgsConfigUnavailable } from "./tokenization-config";
import { type PayoutInput } from "../interfaces/payout";

export type PreparePayoutRailStatus = "ready" | "failed";

export type PreparePayoutRailErrorCode =
  | "PROVISION_FAILED"
  | "REGISTER_FAILED"
  | "READINESS_FAILED"
  /** Recipient-book / legacy PM id not usable under sandbox CVS stub. */
  | "STALE_PAYMENT_METHOD"
  | "GENERIC";

export interface PreparePayoutRailResult {
  status: PreparePayoutRailStatus;
  payout: PayoutInput;
  errorCode?: PreparePayoutRailErrorCode;
  lastError?: string;
}

const isCvsDisabled = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false;
  const code = error.response?.data?.error?.code as string | undefined;
  return code === "CVS_NOT_ENABLED" || code === "CVS_DISABLED";
};

const axiosErrorCode = (error: unknown): string | undefined => {
  if (!axios.isAxiosError(error)) return undefined;
  return error.response?.data?.error?.code as string | undefined;
};

const mapReadinessError = (error: unknown): PreparePayoutRailErrorCode => {
  const code = axiosErrorCode(error);
  if (code === "STUB_ID_REQUIRED" || code === "CARD_NOT_FOUND") {
    return "STALE_PAYMENT_METHOD";
  }
  return "READINESS_FAILED";
};

export const preparePayoutRail = async (args: {
  linkId: string;
  payout: PayoutInput;
  capabilityKey: string;
  cardholderName?: string;
}): Promise<PreparePayoutRailResult> => {
  const { linkId, capabilityKey, cardholderName } = args;
  let payout = args.payout;

  if (payout.source !== "saved" || !payout.paymentMethodId) {
    try {
      const registered = await registerPayoutCard(
        linkId,
        payout,
        cardholderName
      );
      payout = {
        source: "saved",
        paymentMethodId: registered.paymentMethodId,
        brand: registered.brand,
        last4: registered.last4,
        cardExpiry: registered.cardExpiry,
        cardholderName: registered.cardholderName,
      };
    } catch (error) {
      if (isVgsConfigUnavailable(error)) throw error;
      throw Object.assign(new Error("REGISTER_FAILED"), {
        code: "REGISTER_FAILED" as const,
      });
    }
  }

  const paymentMethodId = payout.paymentMethodId;
  if (!paymentMethodId) {
    return {
      status: "failed",
      payout,
      errorCode: "REGISTER_FAILED",
    };
  }

  let readiness;
  try {
    readiness = await destinationCardUsageReadiness(
      linkId,
      paymentMethodId,
      { capabilityKey }
    );
  } catch (error) {
    if (isCvsDisabled(error)) {
      return { status: "ready", payout };
    }
    return {
      status: "failed",
      payout,
      errorCode: mapReadinessError(error),
    };
  }

  if (readiness.ready) {
    return { status: "ready", payout };
  }

  try {
    const provisioned = await provisionDestinationCard(
      linkId,
      paymentMethodId,
      { capabilityKey }
    );

    if (provisioned.ready) {
      return { status: "ready", payout };
    }

    return {
      status: "failed",
      payout,
      errorCode: "PROVISION_FAILED",
      lastError: provisioned.lastError,
    };
  } catch (error) {
    if (isCvsDisabled(error)) {
      return { status: "ready", payout };
    }

    return {
      status: "failed",
      payout,
      errorCode: "PROVISION_FAILED",
    };
  }
};

/** Strip raw PAN after prepare; keep display + id for commit. */
export const sanitizePayoutAfterPrepare = (
  payout: PayoutInput
): PayoutInput => ({
  source: "saved",
  paymentMethodId: payout.paymentMethodId,
  brand: payout.brand,
  last4: payout.last4,
  cardExpiry: payout.cardExpiry,
  cardholderName: payout.cardholderName,
});
